import { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { hostListProperties } from '../api/host';
import { hostBookings as hostBookingsApi } from '../services/bookingsApi';
import { useCurrency } from '../components/CurrencyContext';
import { formatDateRange } from '../utils/dateFormat';

const tabs = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Awaiting Payment', value: 'awaiting_payment' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Checked-in', value: 'checked_in' },
  { label: 'Checked-out', value: 'checked_out' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancel Requests', value: 'cancel_requested' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Declined', value: 'declined' },
];

const toneMap = {
  slate: 'bg-slate-100 text-slate-800',
  emerald: 'bg-emerald-100 text-emerald-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
};

const formatStatusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ');

const StatusChip = ({ label, tone = 'slate' }) => {
  const cls = toneMap[tone] || toneMap.slate;
  return <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${cls}`}>{formatStatusLabel(label)}</span>;
};

const formatRange = (start, end) => formatDateRange(start, end);

const Timeline = ({ events }) => (
  <div className="space-y-2">
    {events.map((evt) => (
      <div key={evt.id} className="flex items-start gap-3 text-sm">
        <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
        <div>
          <div className="font-semibold text-slate-800 capitalize">
            {evt.event_type.replace(/_/g, ' ')} {evt.to_status ? `(${evt.to_status})` : ''}
          </div>
          <div className="text-slate-500 text-xs">{new Date(evt.created_at).toLocaleString()}</div>
          {evt.metadata?.reason && <div className="text-slate-600 text-xs">Reason: {evt.metadata.reason}</div>}
        </div>
      </div>
    ))}
  </div>
);

const PriceBreakdown = ({ booking, formatMoney }) => {
  const totals = booking.totals_json || {};
  const basePrice = totals.base_price ?? totals.base ?? 0;
  const discountAmount = Math.abs(totals.discount_amount ?? totals.discounts ?? 0);
  const taxableSubtotal =
    totals.taxable_subtotal ??
    totals.subtotal_after_discount ??
    Math.max(Number(basePrice || 0) - Number(discountAmount || 0), 0);
  const mandatoryFees =
    totals.mandatory_fees ??
    totals.fees ??
    totals.fee_breakdown?.cleaning_fee ??
    0;
  const gstAmount = totals.gst_amount ?? totals.taxes ?? 0;
  const lines = [
    { label: 'Base Price', value: basePrice },
    discountAmount ? { label: 'Discount', value: -discountAmount } : null,
    { label: 'Taxable Subtotal', value: taxableSubtotal },
    mandatoryFees ? { label: 'Mandatory fees', value: mandatoryFees } : null,
    gstAmount ? { label: 'GST', value: gstAmount } : null,
  ].filter(Boolean);
  return (
    <div className="space-y-1">
      {lines.map((line) => (
        <div key={line.label} className="flex justify-between text-sm text-slate-700">
          <span>{line.label}</span>
          <span>{formatMoney(line.value, booking.currency)}</span>
        </div>
      ))}
      <div className="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-100">
        <span>Final payable amount</span>
        <span>{formatMoney(totals.final_payable ?? totals.total ?? booking.total_amount ?? 0, booking.currency)}</span>
      </div>
      {totals.host_net !== undefined && (
        <div className="flex justify-between text-sm text-emerald-700">
          <span>Host net (after commission)</span>
          <span>{formatMoney(totals.host_net, booking.currency)}</span>
        </div>
      )}
    </div>
  );
};

const HostBookings = ({ accessToken }) => {
  const { formatCurrency } = useCurrency();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', property_id: '', q: '' });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [properties, setProperties] = useState([]);
  const socketRef = useRef(null);
  const [toast, setToast] = useState('');
  const formatMoney = (amount, currency) => formatCurrency(amount, currency || 'INR') || '-';

  const loadProperties = async () => {
    try {
      const res = await hostListProperties(accessToken, { limit: 50 });
      setProperties(res.items || []);
    } catch (_) {
      // ignore
    }
  };

  const load = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await hostBookingsApi.list(accessToken, { ...filters, page, limit: 20 });
      setItems(res.data || res.items || []);
      setPagination(res.pagination || { page, totalPages: 1 });
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (bookingId) => {
    try {
      const res = await hostBookingsApi.get(bookingId, accessToken);
      setSelected(res);
    } catch (err) {
      setError(err.message || 'Failed to load booking detail');
    }
  };

  useEffect(() => {
    loadProperties();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.property_id]);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const socket = io(API_BASE, { transports: ['websocket'], withCredentials: true });
    socketRef.current = socket;
    socket.on('booking_event', async (evt) => {
      if (evt?.booking_id) {
        await load(pagination.page);
        if (selected?.id === evt.booking_id) {
          await loadDetail(evt.booking_id);
        }
        if (evt?.metadata?.auto_confirm) {
          setToast(`Booking ${evt.booking_id} auto-confirmed after payment`);
          setTimeout(() => setToast(''), 5000);
        }
      }
    });
    socket.on('booking_note', (note) => {
      if (note?.booking_id && selected?.id === note.booking_id) {
        loadDetail(note.booking_id);
      }
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;
    items.forEach((b) => socket.emit('join_booking', b.id));
    if (selected?.id) socket.emit('join_booking', selected.id);
  }, [items, selected]);

  const handleSelect = async (b) => {
    setSelected(null);
    await loadDetail(b.id);
  };

  const actions = useMemo(() => {
    const status = selected?.booking_status;
    if (!status) return [];
    const common = [];
    if (status === 'pending' || status === 'awaiting_payment') {
      common.push({ label: 'Accept', action: () => hostBookingsApi.accept(selected.id, accessToken) });
      common.push({
        label: 'Decline',
        action: () => hostBookingsApi.decline(selected.id, 'Declined by host', accessToken),
        tone: 'danger',
      });
    }
    if (status === 'confirmed') {
      common.push({
        label: 'Request cancel',
        action: () => hostBookingsApi.cancelRequest(selected.id, 'Requesting cancellation', accessToken),
      });
      common.push({ label: 'Mark check-in', action: () => hostBookingsApi.checkin(selected.id, accessToken) });
    }
    if (status === 'checked_in') {
      common.push({ label: 'Mark check-out', action: () => hostBookingsApi.checkout(selected.id, accessToken) });
    }
    return common;
  }, [selected, accessToken]);

  const onAction = async (fn) => {
    if (!fn) return;
    setLoading(true);
    try {
      await fn();
      await load(pagination.page);
      if (selected) await loadDetail(selected.id);
    } catch (err) {
      setError(err.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Bookings</p>
          <h1 className="text-2xl font-bold text-slate-900">Booking Management</h1>
        </div>
        {toast && <div className="text-sm px-3 py-2 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-100">{toast}</div>}
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.value || 'all'}
            type="button"
            onClick={() => setFilters((f) => ({ ...f, status: t.value }))}
            className={`px-3 py-1 rounded-full text-sm ${
              filters.status === t.value ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Search booking code or guest"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-64"
          />
          <select
            value={filters.property_id}
            onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value }))}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => load(1)}
            className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm"
          >
            Refresh
          </button>
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-slate-500 text-sm">Loading...</div>}

        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="py-2 px-2">Booking Code</th>
                  <th className="py-2 px-2">Property</th>
                  <th className="py-2 px-2">Guest</th>
                  <th className="py-2 px-2">Dates</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Payment</th>
                  <th className="py-2 px-2">Payout</th>
                  <th className="py-2 px-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => handleSelect(b)}
                    className="hover:bg-emerald-50 cursor-pointer"
                  >
                    <td className="py-2 px-2 font-semibold text-slate-900">{b.booking_code}</td>
                    <td className="py-2 px-2 text-slate-700">{b.property?.title || b.property_title || 'â€”'}</td>
                    <td className="py-2 px-2 text-slate-700">{b.customer_name || b.guest?.name || 'â€”'}</td>
                    <td className="py-2 px-2 text-slate-600">{formatRange(b.check_in_date, b.check_out_date)}</td>
                    <td className="py-2 px-2 text-slate-700 capitalize">{formatStatusLabel(b.booking_status)}</td>
                    <td className="py-2 px-2 text-slate-700 capitalize">
                      {formatStatusLabel(b.payment_status)}
                      <div className="mt-1 text-xs text-slate-500">
                        {formatStatusLabel(b.payment_option_selected || 'full')}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-slate-700 capitalize">{formatStatusLabel(b.payout_status || 'not_ready')}</td>
                    <td className="py-2 px-2 text-slate-900 font-semibold">
                      {formatMoney(b.total_amount || b.totals_json?.total || 0, b.currency)}
                      <div className="mt-1 text-xs font-normal text-slate-500">
                        Paid {formatMoney(b.amount_paid_now ?? b.total_amount ?? 0, b.currency)} | Pending {formatMoney(b.remaining_amount ?? 0, b.currency)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-slate-500">Booking</p>
                <h2 className="text-xl font-semibold text-slate-900">{selected.booking_code}</h2>
                <p className="text-sm text-slate-600">{formatRange(selected.check_in_date, selected.check_out_date)}</p>
              </div>
              <div className="flex gap-2">
                <StatusChip label={selected.booking_status} tone="emerald" />
                <StatusChip label={selected.payment_status} tone="amber" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 uppercase">Guest</p>
                <p className="font-semibold text-slate-900">{selected.customer_name || selected.guest?.name}</p>
                <p className="text-slate-600">{selected.customer_email}</p>
                <p className="text-slate-600">{selected.customer_phone}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 uppercase">Property</p>
                <p className="font-semibold text-slate-900">{selected.property?.title || 'â€”'}</p>
                <p className="text-slate-600">Guests: {selected.guests_count || selected.no_of_guests}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 uppercase">Financials</p>
                <p className="text-slate-700">Payment: {selected.payment_status}</p>
                <p className="text-slate-700">Mode: {formatStatusLabel(selected.payment_option_selected || 'full')}</p>
                <p className="text-slate-700">Paid now: {formatMoney(selected.amount_paid_now ?? selected.total_amount ?? 0, selected.currency)}</p>
                <p className="text-slate-700">Pending: {formatMoney(selected.remaining_amount ?? 0, selected.currency)}</p>
                <p className="text-slate-700">Advance percentage: {selected.partial_payment_percentage ? `${selected.partial_payment_percentage}%` : '-'}</p>
                <p className="text-slate-700">Payout: {selected.payout_status || 'not_ready'}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-slate-100 rounded-lg p-3">
                <p className="text-sm font-semibold text-slate-900 mb-2">Price breakdown</p>
                <PriceBreakdown booking={selected} formatMoney={formatMoney} />
              </div>
              <div className="border border-slate-100 rounded-lg p-3">
                <p className="text-sm font-semibold text-slate-900 mb-2">Timeline</p>
                <Timeline events={selected.events || []} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
            <p className="text-sm font-semibold text-slate-900">Actions</p>
            {actions.length === 0 && <p className="text-sm text-slate-500">No actions available for this status.</p>}
            <div className="flex flex-col gap-2">
              {actions.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => onAction(a.action)}
                  className={`w-full px-3 py-2 rounded-lg text-sm ${
                    a.tone === 'danger'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-emerald-600 text-white'
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>

            <div className="border-t border-slate-100 pt-3">
              <p className="text-sm font-semibold text-slate-900 mb-2">Notes (admin shared)</p>
              {(selected.notes || []).length === 0 && <p className="text-sm text-slate-500">No notes yet.</p>}
              <div className="space-y-2">
                {(selected.notes || []).map((n) => (
                  <div key={n.id} className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-sm">
                    <p className="text-slate-700">{n.note}</p>
                    <p className="text-xs text-slate-500">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostBookings;



